import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { GeneralAuth } from '@services/auth/generalAuth/generalAuth.service';
import { environment } from '@environment/development_environment';

/** URL del endpoint de renovación de token; las peticiones a esta ruta se omiten del interceptor. */
const REFRESH_URL = `${environment.baseUrl}/auth/refresh-token`;

/**
 * Clona la petición HTTP añadiendo el header `Authorization: Bearer <token>`.
 * @param req - Petición HTTP original
 * @param token - JWT de acceso activo
 * @returns Nueva instancia de la petición con el header de autorización
 */
function addAuthHeader(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}

/**
 * Interceptor HTTP de autenticación JWT.
 *
 * Flujo:
 * 1. Omite la petición de refresh-token y los endpoints públicos de auth.
 * 2. Si no hay refresh token (sesión cerrada), deja pasar la petición sin modificar.
 * 3. Si el access token está expirado localmente, lo renueva antes de enviar.
 * 4. Si el token es válido, añade el header `Authorization: Bearer <token>`.
 * 5. Si el servidor responde 401, intenta renovar el token y reenvía la petición.
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const auth = inject(GeneralAuth);

  // No interceptar la propia petición de refresh ni endpoints públicos de auth
  if (req.url.includes(REFRESH_URL) || isPublicEndpoint(req.url)) {
    return next(req);
  }

  // Sin refresh token (no logueado o sesión cerrada): no intentar renovar, dejar pasar la petición
  // para no lanzar "No se pudo renovar el token" en la vista de login
  if (!auth.getRefreshToken()) {
    return next(req);
  }

  // Si el token está expirado localmente, renovar antes de enviar
  if (auth.isAccessTokenExpired()) {
    return auth.refreshToken().pipe(
      switchMap(newToken => {
        if (!newToken) return throwError(() => new Error('No se pudo renovar el token'));
        return next(addAuthHeader(req, newToken));
      }),
      catchError(err => throwError(() => err))
    );
  }

  // Token válido — añadir header si no lo tiene ya
  const token = auth.getAccessToken();
  const outReq = token && !req.headers.has('Authorization')
    ? addAuthHeader(req, token)
    : req;

  return next(outReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && auth.getRefreshToken()) {
        return auth.refreshToken().pipe(
          switchMap(newToken => {
            if (!newToken) return throwError(() => error);
            return next(addAuthHeader(req, newToken));
          }),
          catchError(() => throwError(() => error))
        );
      }
      return throwError(() => error);
    })
  );
};

/**
 * Determina si una URL corresponde a un endpoint público que no requiere autenticación.
 * @param url - URL de la petición HTTP a evaluar
 * @returns `true` si la URL es un endpoint público; `false` en caso contrario
 */
function isPublicEndpoint(url: string): boolean {
  const publicPaths = [
    '/auth/login',
    '/auth/request-recovery-code',
    '/auth/validate-recovery-code',
    '/auth/recovery-change-password',
    '/translations/translate',
  ];
  return publicPaths.some(path => url.includes(path));
}
